import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";
import ReportEditor from "@/components/reports/ReportEditor";
import ConfirmDialog from "@/components/common/ConfirmDialog";

export default function Reports() {
  const { id } = useParams();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => api.get("/reports").then((r) => setReports(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useInvalidate(["reports"], load);

  const current = reports.find((r) => r.id === id) || null;
  const update = (rep) => setReports((rs) => rs.map((r) => (r.id === rep.id ? rep : r)));

  const remove = async () => {
    try { await api.delete(`/reports/${removing.id}`); toast.success("Report deleted"); setRemoving(null); if (id === removing.id) nav("/reports"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full grid grid-cols-[360px_1fr]" data-testid="reports-page">
      <aside className="border-r border-kl-line flex flex-col bg-kl-black">
        <div className="p-6 border-b border-kl-line">
          <p className="kl-label-dark">Proposals</p>
          <h1 className="font-display font-light text-2xl mt-1">Reports <span className="font-mono text-sm text-kl-muted">{reports.length}</span></h1>
        </div>
        <ul className="flex-1 overflow-y-auto kl-scroll">
          {reports.length === 0 && <li className="p-6 text-sm text-kl-muted" data-testid="reports-empty">No proposals yet. Open a device and click “Generate proposal”.</li>}
          {reports.map((r) => (
            <li key={r.id} className={`border-b border-kl-line group ${r.id === id ? "bg-kl-surface" : "hover:bg-kl-surface"} transition-colors duration-150`} data-testid={`report-row-${r.id}`}>
              <div className="flex">
                <button onClick={() => nav(`/reports/${r.id}`)} className="flex-1 text-left p-4 kl-focus">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-sm truncate">{r.client_name}</span>
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${r.status === "sent" ? "border-kl-green text-kl-green" : "border-kl-line text-kl-muted"}`} data-testid={`report-status-${r.id}`}>{r.status}</span>
                  </div>
                  <div className="font-mono text-[11px] text-kl-muted mt-1 truncate">{r.serial}</div>
                  <div className="text-[11px] text-kl-muted mt-1">{r.status === "sent" ? `sent ${fmt.time(r.sent_at)} → ${r.sent_to}` : `edited ${fmt.time(r.updated_at)}`}</div>
                </button>
                <button onClick={() => setRemoving(r)} data-testid={`report-delete-${r.id}`} className="px-3 text-kl-muted opacity-0 group-hover:opacity-100 hover:text-kl-inactive transition-all duration-150"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      </aside>
      <section className="bg-white text-kl-black overflow-y-auto kl-scroll-light">
        {current ? <ReportEditor key={current.id} report={current} onChange={update} /> : (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="report-empty-state">
            <FileText size={28} className="text-kl-mutedLight" />
            <h2 className="font-display text-2xl mt-4">Select a proposal</h2>
            <p className="text-sm text-kl-mutedLight mt-1 max-w-sm">Edit the engineer's note, download the PDF, and send it to the client from here.</p>
          </div>
        )}
      </section>
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Delete this report?" description={removing?.title} confirmLabel="Delete" onConfirm={remove} />
    </div>
  );
}
